/**
 * i18n de la plataforma de RESIDENTE (español default + inglés + portugués).
 *
 * - Librería: i18next + react-i18next.
 * - Idiomas soportados: "es" (default/fallback), "en", "pt-BR".
 * - Los recursos están namespaced por área (common, nav, y una por pantalla que
 *   se va agregando a medida que se traducen).
 * - El idioma efectivo lo maneja `src/context/LanguageContext.jsx`
 *   (localStorage → users.idioma → "es"); acá solo se inicializa i18next con el
 *   idioma guardado en localStorage para el primer render.
 *
 * Códigos de idioma en 3 dominios (usar SIEMPRE los helpers de abajo):
 *   app     → "es" | "en" | "pt-BR"        (i18next / users.idioma)
 *   Intl    → "es-UY" | "en-US" | "pt-BR"  (fechas/números, getLocaleTag)
 *   Meta    → "es" | "en" | "pt_BR"        (plantillas WhatsApp, getMetaLanguage)
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

export const SUPPORTED_LANGUAGES = ["es", "en", "pt-BR"];
export const DEFAULT_LANGUAGE = "es";
export const LANGUAGE_STORAGE_KEY = "habitia_idioma";

// Nombres nativos para el selector (NO se traducen: cada idioma en su lengua).
export const LANGUAGE_NAMES = {
  es: "Español",
  en: "English",
  "pt-BR": "Português",
};

const LOCALE_MAP = {
  es: { intl: "es-UY", meta: "es" },
  en: { intl: "en-US", meta: "en" },
  "pt-BR": { intl: "pt-BR", meta: "pt_BR" },
};

/** Normaliza cualquier código a uno soportado ("pt"→"pt-BR", "en-GB"→"en", etc.). */
export function normalizeLanguage(lang) {
  if (!lang) return DEFAULT_LANGUAGE;
  const l = String(lang).trim();
  if (SUPPORTED_LANGUAGES.includes(l)) return l;
  if (l.toLowerCase().startsWith("pt")) return "pt-BR";
  const base = l.split(/[-_]/)[0].toLowerCase();
  return SUPPORTED_LANGUAGES.includes(base) ? base : DEFAULT_LANGUAGE;
}

/** Tag BCP-47 para Intl/toLocale* (fechas/números). */
export function getLocaleTag(lang) {
  const l = normalizeLanguage(lang || i18n.language);
  return (LOCALE_MAP[l] || LOCALE_MAP[DEFAULT_LANGUAGE]).intl;
}

/** Código de idioma para plantillas de Meta/WhatsApp. */
export function getMetaLanguage(lang) {
  const l = normalizeLanguage(lang || i18n.language);
  return (LOCALE_MAP[l] || LOCALE_MAP[DEFAULT_LANGUAGE]).meta;
}

function initialLanguage() {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) return normalizeLanguage(stored);
  } catch (_) {}
  return DEFAULT_LANGUAGE;
}

/**
 * Auto-carga TODOS los recursos de `./locales/<lng>/<ns>.json` con
 * `require.context` (webpack/CRA). Así agregar una pantalla nueva = solo crear
 * sus 3 JSON (es/en/pt-BR); NO hay que editar este archivo (evita conflictos al
 * traducir muchas pantallas en paralelo).
 */
function buildResources() {
  const ctx = require.context("./locales", true, /\.json$/);
  const resources = {};
  const namespaces = new Set();
  ctx.keys().forEach((key) => {
    const m = key.match(/\.\/([^/]+)\/(.+)\.json$/);
    if (!m) return;
    const [, lng, ns] = m;
    namespaces.add(ns);
    resources[lng] = resources[lng] || {};
    resources[lng][ns] = ctx(key);
  });
  return { resources, namespaces: Array.from(namespaces) };
}

const { resources, namespaces } = buildResources();

i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES,
  ns: namespaces,
  defaultNS: "common",
  interpolation: { escapeValue: false },
  returnEmptyString: false,
  react: { useSuspense: false },
});

export default i18n;
